'use client';

import { c as _c } from "react/compiler-runtime";
import { useChat } from '@ai-sdk/react';
import { useQueryClient } from '@tanstack/react-query';
import { DefaultChatTransport } from 'ai';
import { useLayoutEffect, useMemo, useRef } from 'react';
import { useEventCallback } from 'usehooks-ts';
import type { SidebarChatEngine, SidebarChatTurnEnd } from './sidebar-chat-engine';
import type { AiContext } from './types/ai-context';
import type { SidebarChatMessage } from './types/sidebar-chat-message';
import { buildSidebarChatTransportBody, prepareSidebarChatSendMessagesRequest } from './types/chat-request';
import { messagesQueryKey } from '@/data/messages/query-keys';
import { useMessages } from '@/data/messages/use-messages';
import { useChatStore } from '@/lib/stores/chat-store';
import { usePrStore } from '@/lib/stores/pr-store';
type ToolCallEvent = {
  toolCall: {
    toolCallId: string;
    toolName: string;
    input: unknown;
  };
};

/** The sidebar's own chat as the page shows it: its engine and the saved conversation it goes on with. */
export type SidebarSdkChat = {
  engine: SidebarChatEngine;
  /** The chat's saved messages, once read; empty for a new chat. */
  savedMessages: SidebarChatMessage[];
  /** The chat's saved messages are still loading: the chat takes no message until they arrive. */
  historyLoading: boolean;
  /** Reading the saved messages failed and nothing is cached: try again. */
  retryHistory: (() => void) | null;
};

/**
 * The sidebar's own chat: the AI SDK's `useChat`, answered by `/api/chat`. It
 * mounts with the page's chat view, so the composer works at once; a saved
 * chat's messages seed it once they arrive, before anything is sent, since
 * `useChat` reads `messages` only when it mounts.
 */
export function useSidebarSdkChat(t0) {
  const $ = _c(43);
  const {
    id,
    isPersisted,
    enabled,
    resolveRequestContext,
    onToolCall,
    onTurnEnd
  } = t0;
  const queryClient = useQueryClient();
  const setChatPersisted = useChatStore(_temp);
  const {
    installationId,
    pullRequestId
  } = usePrStore();
  const readsHistory = enabled && isPersisted;
  const {
    data: savedMessages,
    isPending,
    isError,
    refetch
  } = useMessages(id, readsHistory);
  let t1;
  if ($[0] !== id || $[1] !== installationId || $[2] !== pullRequestId || $[3] !== resolveRequestContext) {
    t1 = async () => buildSidebarChatTransportBody({
      id,
      context: await resolveRequestContext(),
      installationId,
      pullRequestId
    });
    $[0] = id;
    $[1] = installationId;
    $[2] = pullRequestId;
    $[3] = resolveRequestContext;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const resolveTransportBody = useEventCallback(t1);
  let t2;
  if ($[5] !== resolveTransportBody) {
    t2 = new DefaultChatTransport({
      body: resolveTransportBody,
      prepareSendMessagesRequest: prepareSidebarChatSendMessagesRequest
    });
    $[5] = resolveTransportBody;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const transport = t2;
  let t3;
  if ($[7] !== id) {
    t3 = () => useChatStore.getState().chatId === id;
    $[7] = id;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const isCurrentChat = t3;
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== isCurrentChat || $[11] !== setChatPersisted) {
    t5 = error => {
      if (!isCurrentChat()) {
        return;
      }
      console.error("Chat error:", error);
      if (error.message?.includes("401") || error.message?.includes("404")) {
        setChatPersisted(false);
      }
    };
    $[10] = isCurrentChat;
    $[11] = setChatPersisted;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== id || $[14] !== isCurrentChat || $[15] !== onTurnEnd || $[16] !== queryClient || $[17] !== setChatPersisted) {
    t6 = t7 => {
      const {
        messages: finishedMessages,
        isAbort,
        isDisconnect,
        isError: failed
      } = t7;
      if (!isCurrentChat()) {
        return;
      }
      queryClient.setQueryData(messagesQueryKey(id), finishedMessages);
      setChatPersisted(true);
      onTurnEnd({
        lastMessageId: finishedMessages.at(-1)?.id ?? null,
        completed: !isAbort && !isDisconnect && !failed
      });
    };
    $[13] = id;
    $[14] = isCurrentChat;
    $[15] = onTurnEnd;
    $[16] = queryClient;
    $[17] = setChatPersisted;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== id || $[20] !== onToolCall || $[21] !== t5 || $[22] !== t6 || $[23] !== transport) {
    t7 = {
      messages: t4,
      id,
      transport,
      experimental_throttle: 16,
      onToolCall,
      onError: t5,
      onFinish: t6
    };
    $[19] = id;
    $[20] = onToolCall;
    $[21] = t5;
    $[22] = t6;
    $[23] = transport;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  const engine = useChat(t7);
  const seeded = useRef(false);
  const {
    messages,
    setMessages
  } = engine;
  let t8;
  let t9;
  if ($[25] !== messages.length || $[26] !== savedMessages || $[27] !== setMessages) {
    t8 = () => {
      if (seeded.current || !savedMessages || savedMessages.length === 0) {
        return;
      }
      seeded.current = true;
      if (messages.length === 0) {
        setMessages(savedMessages);
      }
    };
    t9 = [messages.length, savedMessages, setMessages];
    $[25] = messages.length;
    $[26] = savedMessages;
    $[27] = setMessages;
    $[28] = t8;
    $[29] = t9;
  } else {
    t8 = $[28];
    t9 = $[29];
  }
  useLayoutEffect(t8, t9);
  let t10;
  if ($[30] !== readsHistory || $[31] !== savedMessages) {
    t10 = readsHistory ? savedMessages ?? [] : [];
    $[30] = readsHistory;
    $[31] = savedMessages;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  const t11 = readsHistory && isPending;
  let t12;
  if ($[33] !== isError || $[34] !== readsHistory || $[35] !== refetch || $[36] !== savedMessages) {
    t12 = readsHistory && isError && !savedMessages ? () => void refetch() : null;
    $[33] = isError;
    $[34] = readsHistory;
    $[35] = refetch;
    $[36] = savedMessages;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  let t13;
  if ($[38] !== engine || $[39] !== t10 || $[40] !== t11 || $[41] !== t12) {
    t13 = {
      engine,
      savedMessages: t10,
      historyLoading: t11,
      retryHistory: t12
    };
    $[38] = engine;
    $[39] = t10;
    $[40] = t11;
    $[41] = t12;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  return t13;
}
function _temp(state) {
  return state.setChatPersisted;
}